import React, { useEffect, useState, useMemo } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";
import {
  Activity, Clock, MapPin, Users, TrendingUp, RefreshCw, Loader2,
  FileDown, FileSpreadsheet, Calendar, Settings,
} from "lucide-react";

const COLORS = ["#FFC60A", "#0A0A0B", "#F97316", "#22C55E", "#EF4444", "#6366F1"];

const PERIODS = [
  { id: "week", label: "Hebdo", days: 7 },
  { id: "month", label: "Mensuel", days: 30 },
  { id: "quarter", label: "Trimestriel", days: 90 },
];

export default function Analytics() {
  const [report, setReport] = useState(null);
  const [legacy, setLegacy] = useState(null); // existing /analytics/overview for charts
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // 'pdf' | 'xlsx' | null
  const [costPerKm, setCostPerKm] = useState(0);
  const [costSaving, setCostSaving] = useState(false);
  const [showCostPanel, setShowCostPanel] = useState(false);

  const load = async (p = period) => {
    setLoading(true);
    try {
      const days = (PERIODS.find((x) => x.id === p) || PERIODS[1]).days;
      const [reportRes, legacyRes, settingsRes] = await Promise.all([
        api.get(`/reports/data?period=${p}`),
        api.get(`/analytics/overview?days=${days}`),
        api.get("/reports/settings"),
      ]);
      setReport(reportRes.data);
      setLegacy(legacyRes.data);
      setCostPerKm(Number(settingsRes.data?.cost_per_km || 0));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const presenceTop = useMemo(() => (legacy?.presence_rate || []).slice(0, 10), [legacy]);
  const driverTop = useMemo(() => (legacy?.driver_performance || []).slice(0, 8), [legacy]);
  const statusPie = useMemo(() => {
    if (!legacy?.missions_daily) return [];
    let completed = 0, active = 0, other = 0;
    legacy.missions_daily.forEach((d) => {
      completed += d.completed || 0; active += d.active || 0; other += d.other || 0;
    });
    return [
      { name: "Terminées", value: completed },
      { name: "Actives", value: active },
      { name: "Autres", value: other },
    ].filter((x) => x.value > 0);
  }, [legacy]);

  const handleExport = async (fmt) => {
    setExporting(fmt);
    try {
      const token = localStorage.getItem("st_access_token");
      const url = `${API}/reports/export.${fmt}?period=${period}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(`Export failed: ${detail.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext = fmt === "pdf" ? "pdf" : "xlsx";
      const ts = new Date().toISOString().slice(0, 16).replace("T", "_").replace(":", "h");
      a.href = downloadUrl;
      a.download = `logibus_rapport_${period}_${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Rapport ${fmt.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(e.message || "Erreur d'export");
    } finally {
      setExporting(null);
    }
  };

  const saveCost = async () => {
    setCostSaving(true);
    try {
      await api.post("/reports/settings", { cost_per_km: Number(costPerKm) || 0 });
      toast.success("Coût/km mis à jour");
      setShowCostPanel(false);
      await load(period); // refresh KPIs
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setCostSaving(false);
    }
  };

  const kpis = report?.synthesis?.kpis;
  const periodLabel = report?.synthesis?.period_label || "—";

  return (
    <div className="p-4 lg:p-6" data-testid="analytics-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Analytics · LOGIBUS</div>
          <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter">Rapports & Performance</h1>
          <p className="text-sm text-zinc-600 mt-1">{periodLabel}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="border border-zinc-900 grid grid-cols-3 text-[10px] font-bold uppercase tracking-widest" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`period-${p.id}`}
                className={`px-3 py-2 border-r border-zinc-900 last:border-r-0 ${period === p.id ? "bg-[#FFC60A]" : "bg-white hover:bg-zinc-50"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={() => load(period)} data-testid="refresh-analytics"
            disabled={loading}
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white disabled:opacity-50">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Rafraîchir
          </button>
          <button onClick={() => handleExport("pdf")} data-testid="export-pdf-btn"
            disabled={exporting === "pdf" || loading}
            className="inline-flex items-center gap-2 border border-zinc-900 bg-zinc-900 text-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50">
            {exporting === "pdf" ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
            Export PDF
          </button>
          <button onClick={() => handleExport("xlsx")} data-testid="export-xlsx-btn"
            disabled={exporting === "xlsx" || loading}
            className="inline-flex items-center gap-2 border border-zinc-900 bg-[#FFC60A] text-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-yellow-300 disabled:opacity-50">
            {exporting === "xlsx" ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
            Export Excel
          </button>
          <button onClick={() => setShowCostPanel((s) => !s)} data-testid="cost-settings-toggle"
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100">
            <Settings size={12} /> Coût/km
          </button>
        </div>
      </header>

      {showCostPanel && (
        <div className="border border-zinc-900 bg-zinc-50 p-4 mb-6" data-testid="cost-settings-panel">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-1">Coût opérationnel par kilomètre (€)</div>
              <input
                type="number" min="0" step="0.01"
                value={costPerKm}
                onChange={(e) => setCostPerKm(e.target.value)}
                data-testid="cost-per-km-input"
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white"
                placeholder="0.00"
              />
            </div>
            <button onClick={saveCost} disabled={costSaving} data-testid="save-cost-btn"
              className="border border-zinc-900 bg-zinc-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50">
              {costSaving ? "..." : "Enregistrer"}
            </button>
            <button onClick={() => setShowCostPanel(false)}
              className="border border-zinc-900 bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100">
              Annuler
            </button>
          </div>
          <p className="text-[10px] font-mono mt-2 text-zinc-500">
            Utilisé pour estimer le coût opérationnel total (Coût = Coût/km × Km parcourus). Disponible dans les rapports PDF & Excel.
          </p>
        </div>
      )}

      {kpis && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-900 mb-3" data-testid="kpi-grid-primary">
            <Kpi label="Missions" value={kpis.total_missions} icon={Activity} />
            <Kpi label="Taux complétion" value={`${kpis.completion_rate} %`} icon={TrendingUp} />
            <Kpi label="Retards" value={kpis.total_delays} icon={Clock} accent="bg-orange-50" />
            <Kpi label="Retard moyen" value={`${kpis.avg_delay_min} min`} icon={Clock} last />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-900 mb-6" data-testid="kpi-grid-secondary">
            <Kpi label="Ponctualité" value={`${kpis.on_time_rate} %`} icon={Activity} />
            <Kpi label="Kilomètres" value={`${kpis.total_km} km`} icon={MapPin} />
            <Kpi label="Bus actifs" value={`${kpis.active_buses}/${kpis.total_buses}`} icon={MapPin} accent="bg-[#FFF7D6]" />
            <Kpi
              label={kpis.cost_per_km > 0 ? "Coût estimé" : "Coût (non config.)"}
              value={kpis.cost_per_km > 0 ? `${kpis.estimated_cost_eur.toFixed(2)} €` : "—"}
              icon={Users}
              last
            />
          </div>
        </>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Missions par jour" subtitle="Terminées vs actives">
          <ChartOrEmpty data={legacy?.missions_daily} testId="chart-missions-daily">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={legacy?.missions_daily || []} margin={{ left: -10, right: 10, top: 8 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFC60A" stopOpacity={0.85} />
                    <stop offset="100%" stopColor="#FFC60A" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#52525B" />
                <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#52525B" />
                <Tooltip wrapperStyle={{ border: "1px solid #0A0A0B", fontFamily: "monospace", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="completed" stroke="#0A0A0B" strokeWidth={2} fill="url(#g1)" name="Terminées" />
                <Area type="monotone" dataKey="active" stroke="#F97316" strokeWidth={2} fillOpacity={0.15} fill="#F97316" name="Actives" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartOrEmpty>
        </Card>

        <Card title="Retards par jour" subtitle={`${legacy?.totals?.delays || 0} incidents délai`}>
          <ChartOrEmpty data={legacy?.delays_daily} testId="chart-delays-daily">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={legacy?.delays_daily || []} margin={{ left: -10, right: 10, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#52525B" />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#52525B" />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#52525B" />
                <Tooltip wrapperStyle={{ border: "1px solid #0A0A0B", fontFamily: "monospace", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="left" type="monotone" dataKey="count" stroke="#EF4444" strokeWidth={2} dot={{ r: 2 }} name="Nb retards" />
                <Line yAxisId="right" type="monotone" dataKey="avg_min" stroke="#0A0A0B" strokeWidth={2} dot={{ r: 2 }} name="Min moy." strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </ChartOrEmpty>
        </Card>

        <Card title="Top chauffeurs" subtitle="Missions terminées (toutes périodes)">
          <ChartOrEmpty data={driverTop} testId="chart-drivers">
            <ResponsiveContainer width="100%" height={Math.max(220, driverTop.length * 32)}>
              <BarChart data={driverTop} layout="vertical" margin={{ left: 60, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#52525B" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#0A0A0B" width={120} />
                <Tooltip wrapperStyle={{ border: "1px solid #0A0A0B", fontFamily: "monospace", fontSize: 11 }} />
                <Bar dataKey="completed" fill="#FFC60A" stroke="#0A0A0B" strokeWidth={1} name="Terminées" />
              </BarChart>
            </ResponsiveContainer>
          </ChartOrEmpty>
        </Card>

        <Card title="Taux de présence (top 10 enfants)" subtitle="Embarquements / réservations confirmées">
          <ChartOrEmpty data={presenceTop} testId="chart-presence">
            <ResponsiveContainer width="100%" height={Math.max(220, presenceTop.length * 28)}>
              <BarChart data={presenceTop} layout="vertical" margin={{ left: 60, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#52525B" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#0A0A0B" width={120} />
                <Tooltip wrapperStyle={{ border: "1px solid #0A0A0B", fontFamily: "monospace", fontSize: 11 }}
                  formatter={(v, n, p) => [`${v}% · ${p.payload.present}/${p.payload.expected}`, "Taux"]} />
                <Bar dataKey="rate" fill="#22C55E" stroke="#0A0A0B" strokeWidth={1} />
              </BarChart>
            </ResponsiveContainer>
          </ChartOrEmpty>
        </Card>

        <Card title="Répartition statuts missions" subtitle="Cumul sur la fenêtre">
          <ChartOrEmpty data={statusPie} testId="chart-status-pie">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={88} label={(e) => `${e.name} (${e.value})`}>
                  {statusPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#0A0A0B" strokeWidth={1} />)}
                </Pie>
                <Tooltip wrapperStyle={{ border: "1px solid #0A0A0B", fontFamily: "monospace", fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartOrEmpty>
        </Card>

        <Card title="Activité globale" subtitle="Cadence quotidienne missions + retards">
          <ChartOrEmpty data={legacy?.missions_daily} testId="chart-activity">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={legacy?.missions_daily || []} margin={{ left: -10, right: 10, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#52525B" />
                <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#52525B" />
                <Tooltip wrapperStyle={{ border: "1px solid #0A0A0B", fontFamily: "monospace", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="completed" stackId="a" fill="#FFC60A" stroke="#0A0A0B" strokeWidth={1} name="Terminées" />
                <Bar dataKey="active" stackId="a" fill="#F97316" stroke="#0A0A0B" strokeWidth={1} name="Actives" />
                <Bar dataKey="other" stackId="a" fill="#A1A1AA" stroke="#0A0A0B" strokeWidth={1} name="Autres" />
              </BarChart>
            </ResponsiveContainer>
          </ChartOrEmpty>
        </Card>
      </div>

      {report && (
        <div className="mt-6 border border-zinc-900 bg-white" data-testid="export-summary">
          <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-base tracking-tight">Contenu des exports</div>
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">
                Données aggrégées pour PDF & Excel
              </div>
            </div>
            <Calendar size={16} className="text-zinc-500" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 text-xs">
            {[
              ["Exploitation (lignes)", report.exploitation.length],
              ["Chauffeurs", report.driver_performance.length],
              ["Flotte (véhicules)", report.fleet.length],
              ["Présence enfants", report.presence.length],
              ["Incidents", report.incidents.length],
              ["Jours analysés", report.synthesis.days],
              ["Jours avec retard", report.delays_daily.length],
              ["Jours avec missions", report.missions_daily.length],
            ].map(([label, n]) => (
              <div key={label} className="p-3 border-r border-b border-zinc-200 last:border-r-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
                <div className="font-display font-bold text-xl mt-1">{n}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="border border-zinc-900 bg-white" data-testid={`analytics-card-${title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '')}`}>
      <div className="px-4 py-3 border-b border-zinc-900 flex items-end justify-between gap-3">
        <div>
          <div className="font-display font-bold text-base tracking-tight">{title}</div>
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{subtitle}</div>
        </div>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent = "bg-white", last = false }) {
  return (
    <div className={`p-4 border-r border-zinc-900 ${last ? "border-r-0" : ""} ${accent}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{label}</div>
        <Icon size={14} className="text-zinc-500" />
      </div>
      <div className="mt-2 font-display font-black text-3xl tracking-tighter">{value}</div>
    </div>
  );
}

function ChartOrEmpty({ data, children, testId }) {
  const empty = !data || (Array.isArray(data) && data.length === 0);
  if (empty) {
    return (
      <div className="h-[220px] flex items-center justify-center text-xs font-mono text-zinc-400 italic"
           data-testid={`${testId}-empty`}>
        Aucune donnée disponible pour cette période.
      </div>
    );
  }
  return children;
}
